// The game's music tracks. Since 2026-10-22 the game plays recorded tracks
// (createFileTrack, below; which ones: music-controller.ts). "Night run" and
// "Lounge", the procedural tracks picked in the sound lab (/debug/sound-lab) on
// 2026-10-20, stay here for the lab. All are MusicSources (music-sources.ts).
import type { MusicSource } from "./music-sources";
import { hat, kick, mtof, note as N, pad, pluck } from "./synth";
import { asset } from "../shared/portal-env";

// The lookahead scheduler, as in music-sources.ts.
export function sequencer(ctx: AudioContext, bpm: number, onStep: (step: number, bar: number, time: number, intensity: number, sps: number) => void) {
  const sps = 60 / bpm / 4;
  let timer: ReturnType<typeof setInterval> | null = null;
  let next = 0, step = 0, intensity = 0;
  const tick = () => {
    while (next < ctx.currentTime + 0.12) {
      onStep(step % 16, Math.floor(step / 16), next, intensity, sps);
      next += sps;
      step++;
    }
  };
  return {
    start() { if (timer) return; step = 0; next = ctx.currentTime + 0.06; tick(); timer = setInterval(tick, 25); },
    stop() { if (timer) clearInterval(timer); timer = null; },
    setIntensity(v: number) { intensity = Math.max(0, Math.min(1, v)); },
  };
}

// A track's output chain: everything through a lowpass that opens with intensity.
export function chain(ctx: AudioContext, low: number, high: number) {
  const tone = ctx.createBiquadFilter();
  tone.type = "lowpass";
  tone.frequency.value = low;
  const output = ctx.createGain();
  tone.connect(output);
  let cut = low;
  return { tone, output, open(time: number, intensity: number) { cut += (low + intensity * (high - low) - cut) * 0.08; tone.frequency.setTargetAtTime(cut, time, 0.06); } };
}

// Night run: darker and tighter, 124 BPM. A 16th-note bass pulse that ducks on
// every beat, sparse stabs; the drums arrive late.
export function createNightRun(ctx: AudioContext): MusicSource {
  const { tone, output, open } = chain(ctx, 500, 7000);
  const roots = [N("D", 2), N("D", 2), N("A#", 1), N("C", 2)];
  const seq = sequencer(ctx, 124, (s, bar, t, i, sps) => {
    open(t, i);
    const r = roots[bar % 4];
    pluck(ctx, tone, { freq: mtof(r + (s % 8 === 6 ? 12 : 0)), time: t, duration: sps * 0.9, type: "sawtooth", gain: s % 4 === 0 ? 0.04 : 0.09, cutoff: 500 + i * 1500 });
    if (s === 0 || s === 10) pluck(ctx, tone, { freq: mtof(r + 24 + 3), time: t, duration: sps * 3, type: "square", gain: 0.03 + i * 0.03, cutoff: 2500 });
    if (s === 0 && bar % 2 === 0) pad(ctx, tone, { freqs: [r + 24, r + 27, r + 31].map(mtof), time: t, duration: sps * 32, gain: 0.025, attack: 1, release: 1, cutoff: 1200 });
    if (i > 0.35 && s % 4 === 2) hat(ctx, tone, t, 0.08, true);
    if (i > 0.55 && s % 4 === 0) kick(ctx, tone, t, 0.8);
    if (i > 0.75 && s % 2 === 1) hat(ctx, tone, t, 0.04);
  });
  return { output, start: seq.start, stop: seq.stop, setIntensity: seq.setIntensity };
}

// Lounge: menu music, 84 BPM, no drums. Warm pads and a lazy pluck melody.
export function createLounge(ctx: AudioContext): MusicSource {
  const { tone, output, open } = chain(ctx, 2200, 3200);
  const prog = [[N("A", 3), N("C", 4), N("E", 4), N("G", 4)], [N("F", 3), N("A", 3), N("C", 4), N("E", 4)], [N("D", 3), N("F", 3), N("A", 3), N("C", 4)], [N("E", 3), N("G", 3), N("B", 3), N("D", 4)]];
  const mel = [0, -1, 2, -1, -1, 3, -1, 1, 0, -1, -1, 2, -1, 3, -1, -1];
  const seq = sequencer(ctx, 84, (s, bar, t, i, sps) => {
    open(t, i);
    const ch = prog[bar % 4];
    if (s === 0) pad(ctx, tone, { freqs: ch.map(mtof), time: t, duration: sps * 16, gain: 0.03, attack: 0.8, release: 1.2, cutoff: 1600 });
    if (s === 0 || s === 8) pluck(ctx, tone, { freq: mtof(ch[0] - 12), time: t, duration: sps * 7, type: "sine", gain: 0.12 });
    const m = mel[(s + bar * 5) % 16];
    if (m >= 0) pluck(ctx, tone, { freq: mtof(ch[m] + 12), time: t, duration: sps * 4, type: "triangle", gain: 0.06, cutoff: 3000 });
  });
  return { output, start: seq.start, stop: seq.stop, setIntensity: seq.setIntensity };
}


// A recorded track (public/music/), looped, as a MusicSource. Fetched and
// decoded when first made; a start() before it has arrived plays it once it has.
// It picks up where it last stopped, so every round doesn't restart the intro.
// OGG where the browser decodes it (exact loops), the AAC copy elsewhere (Safari).
// `level` matches it to the procedural tracks' loudness (recordings are mastered
// far louder); `react` makes it answer intensity a little: a darker, quieter
// start that opens up as the field thins.
export function createFileTrack(ctx: AudioContext, name: string, opts: { level: number; react?: boolean }): MusicSource {
  const { tone, output, open } = chain(ctx, opts.react ? 5000 : 20000, 20000);
  const lift = ctx.createGain();
  lift.gain.value = opts.level;
  lift.connect(tone);
  let buffer: AudioBuffer | null = null;
  let src: AudioBufferSourceNode | null = null;
  let wanted = false, startedAt = 0, offset = 0, intensity = 0;
  let timer: ReturnType<typeof setInterval> | null = null;

  const ogg = typeof Audio !== "undefined" && new Audio().canPlayType('audio/ogg; codecs="vorbis"') !== "";
  fetch(asset(`/music/${name}.${ogg ? "ogg" : "m4a"}`))
    .then((r) => r.arrayBuffer())
    .then((b) => ctx.decodeAudioData(b))
    .then((b) => { buffer = b; if (wanted) play(); })
    .catch(() => { /* no file: silence */ });

  function play() {
    if (!buffer || src) return;
    src = ctx.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(lift);
    startedAt = ctx.currentTime - offset;
    src.start(0, offset % buffer.duration);
    if (opts.react) timer = setInterval(() => {
      open(ctx.currentTime, intensity);
      lift.gain.setTargetAtTime(opts.level * (0.8 + intensity * 0.2), ctx.currentTime, 0.3);
    }, 50);
  }
  return {
    output,
    start() { wanted = true; play(); },
    stop() {
      wanted = false;
      if (src) {
        if (buffer) offset = (ctx.currentTime - startedAt) % buffer.duration;
        try { src.stop(); } catch { /* already stopped */ }
        src.disconnect();
        src = null;
      }
      if (timer) clearInterval(timer);
      timer = null;
    },
    setIntensity(v: number) { intensity = Math.max(0, Math.min(1, v)); },
  };
}
